import React from "react";
import "../../../campaign/styles/BroadcastTable.css";
import "../../../campaign/styles/Preview.css";

import "../../../../components/commonComponents/tables/Table.css";

import { ICONS } from "../../../../assets/icons";
import { SegmentHeaderList as audienceColumn } from "../../../../constant/app/campaign/Broadcast";

// Filter the header list based on campaignEvaluationType
const getFilteredHeaderList = (campaignEvaluationType) => {
  // Default case - show all columns except Sales Amount and Sales Count
  if (!campaignEvaluationType || (campaignEvaluationType !== 'amount' && campaignEvaluationType !== 'volume')) {
    return audienceColumn.filter(
      item => item.name !== 'Sales Amount' && item.name !== 'Sales Count'
    );
  }

  if (campaignEvaluationType === 'amount') {
    // For 'amount' type, include Sales Amount but exclude Leads, Clicks, and Sales Count
    return audienceColumn.filter(
      item => item.name !== 'Leads' &&
              item.name !== 'Clicks' &&
              item.name !== 'Sales Count'
    );
  }

  // For 'volume' type, include Sales Count but exclude Leads, Clicks, and Sales Amount
  return audienceColumn.filter(
    item => item.name !== 'Leads' &&
            item.name !== 'Clicks' &&
            item.name !== 'Sales Amount'
  );
};

const formatTimestamp = (timestamp) => {
  const dt = new Date(timestamp);
  const options = {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    hourCycle: "h12",
  };

  let formattedDate = dt.toLocaleString("en-US", options).toLowerCase();

  formattedDate = formattedDate.replace(/\b\w/g, (match) =>
    match.toUpperCase()
  );

  return formattedDate;
};

/**
 * Formats an ISO 8601 timestamp string to a readable local time string
 * @param {string} isoString - ISO 8601 timestamp string (e.g., "2025-08-17T07:30:00+05:30" or "2025-08-15T02:03:42.286Z")
 * @returns {string} Formatted date string (e.g., "Aug 17, 12:30 PM")
 */
const formatISOTimestamp = (isoString) => {
  if (!isoString) return '';
  
  const dt = new Date(isoString);
  
  // Format options (same as formatTimestamp for consistency)
  const options = {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    hourCycle: "h12",
  };

  // Format the date in the user's local timezone
  let formattedDate = dt.toLocaleString("en-US", options).toLowerCase();

  // Capitalize first letter of each word
  formattedDate = formattedDate.replace(/\b\w/g, (match) => 
    match.toUpperCase()
  );

  return formattedDate;
};

const AudienceSegmentReport = ({ segment }) => {
  const campaignEvaluationType = segment?.settings?.campaign_goal_evaluation_type;
  const filteredHeaders = getFilteredHeaderList(campaignEvaluationType);
  const columnLength = filteredHeaders.length;
  const segments = segment?.settings?.segment_settings;

  // Create a map of field names to their values for easy lookup
  const getFieldValue = (item, fieldName) => {
    if (!item) return 0;

    const fieldMap = {
      'Audience': item?.total_count || item?.audience_count || 0,
      'Sent': item?.sent_count || 0,
      'Delivered': item?.delivered_count || 0,
      'Opened': item?.opened_count || 0,
      'Clicks': item?.clicks_count || 0,
      'Leads': item?.leads_count || 0,
      'Bounced': item?.bounced_count || 0,
      'Opted Out': item?.opted_out_count || 0,
      'Sales Amount': item?.total_sales_amount
        ? (Number(item.total_sales_amount) / 100).toFixed(2)
        : (item.sales_amount ? (Number(item.sales_amount) / 100).toFixed(2) : '0.00'),
      'Sales Count': item?.total_sales_count || item?.sales_count || 0
    };

    const value = fieldMap[fieldName];
    return value !== undefined ? value : 0;
  };

  return (
    <div
      style={{
        overflow: "scroll",
      }}
    >
      <div className=" flex-row">
        <table
          style={{
            padding: "0 16px 0 0",
            width: columnLength > 7 ? `${200 + columnLength * 50}px` : "100%",
          }}
        >
          <div className="flex-row w-[87vw] align-center px-2">
            <div className="segment_name flex-row w-20">Segment details</div>
            <div className="w-80 flex-row align-center">
              {filteredHeaders.map((column) => (
              <th
                key={column?.name}
                className="header_name w-[10vw] text-center"
              >
                {column?.name}
              </th>
            ))}
            </div>
          </div>

          <>
            {segments?.map((item, index) => (
              <div className="Audience_list_main" key={item?.id}>
                {/* Audience List */}
                <div className="flex-row align-center px-5 py-2">
                  <div className="w-20 flex-row align-center">
                    <img src={ICONS?.BroadcastSegmentIcon} alt="segment" />
                    <span>Audience List: {index + 1}</span>
                  </div>
                  <tr className="w-80 flex-row align-center text-[0.729vw] audi">
                    {filteredHeaders.map((header) => (
                      <td
                        key={header.id}
                        className={`w-[10vw] flex-row justify-center ${header.name !== 'Audience' ? 'pl-10' : ''}`}
                      >
                        <div className="flex-row align-center justify-center">
                          {header.name === 'Sales Amount' ? `₹${getFieldValue(item, header.name)}` : getFieldValue(item, header.name)}
                        </div>
                      </td>
                    ))}
                  </tr>
                </div>

                {/* Segment List */}
                <div className="segment_list">
                  <div className="flex-column">
                    {item?.segments?.map((segmentItem) => (
                      <div className="flex-row w-100" key={segmentItem?.id}>
                        <div className="w-20 flex-column">
                          <div className="flex-row align-center">
                            <img
                              src={ICONS?.BroadcastNewSegmentIcon}
                              alt="segment"
                              style={{ width: "10px", height: "10px" }}
                            />
                            <span>Segment - {segmentItem?.id}</span>
                          </div>
                          <div className="segment_column flex-row align-center">
                            {segmentItem?.status === 12 && (
                              <span>Completed @</span>
                            )}
                            {segmentItem?.status === 11 && <span>Running</span>}
                            {segmentItem?.status === 10 && (
                              <span>Starts @</span>
                            )}

                            <img
                              src={ICONS?.calenderScheduled}
                              alt=""
                              style={{ width: "15px", height: "15px" }}
                            />
                            {(segmentItem?.status === 12 || segmentItem?.status === 11) && (
                              <span>
                                {formatISOTimestamp(segmentItem?.updated_at)}
                              </span>
                            )}
                            {segmentItem?.status === 10 && (
                              <span>
                                {formatISOTimestamp(segmentItem?.schedule_at || segmentItem?.schedule_on)}
                              </span>
                            )}
                          </div>
                        </div>
                        <tr className="Right-sight w-80 flex-row space-between text-[0.729vw] align-center">
                          {filteredHeaders.map((header) => {
                            const value = getFieldValue(segmentItem, header.name);
                            return (
                              <td key={header.id}>
                                <span className="flex-row w-[8vw] justify-center">
                                  {header.name === 'Sales Amount' ? `₹${value}` : value}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </>
        </table>
      </div>
    </div>
  );
};

export default AudienceSegmentReport;
